// 成員分頁（SPEC #org-model-membership、IAM-01 / 07 / 08）：個人組織與 team 組織同一套。
//   - 成員清單：只列本組織的資料；角色（owner / 管理員 / 成員 / 訪客；只有 owner 能給 / 拿掉管理員）、停用 / 恢復、組織層權限、
//     分享給本組織的設備用的服務角色（service_roles，只能在分享範圍內）、值班模式
//   - 邀請：一律對 email——已有帳號直接加入；沒有就寄邀請信，畫面顯示邀請連結可複製（7 天）
//   - 移出成員：只收回這個組織給的授權；他是家戶管理人時先交接，沒有接任者才設為無人管理
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { useApp } from '../../lib/AppContext';
import { useConfirm } from '../../components/ConfirmDialog';
import { MoreMenu, type MoreItem } from '../../components/MoreMenu';
import { acceptInviteLink, assignableRoles, isOrgAdmin, orgApi, orgShareApi, SERVICE_ROLES, type InviteResult, type MyOrg, type OrgMember } from '../../lib/planeApi';
import { Dialog } from '../../components/Dialog';
import { errText } from '../settings/OrgSection';
import { RemoveDialog } from './MemberDialogs';
import { dateStamp } from '../../lib/timefmt';
import { useEmailVerifyPrompt } from '../../components/EmailVerifyPrompt';

const ORG_PERMS = ['org.sites', 'org.devices', 'org.audit', 'org.billing', 'org.support'] as const;


export function memberName(m: Pick<OrgMember, 'display_name' | 'email' | 'user_id'> | undefined, fallback = ''): string {
  if (!m) return fallback;
  return m.display_name || m.email || m.user_id;
}

/** 我能不能改這位成員的角色（owner 不能改；管理員只有 owner 能動）。 */
export function canChangeRole(org: Pick<MyOrg, 'role'>, m: Pick<OrgMember, 'role' | 'is_me'>): boolean {
  if (!isOrgAdmin(org) || m.role === 'owner' || m.is_me) return false;
  return org.role === 'owner' || m.role !== 'org_admin';
}

/**
 * 成員的管理動作（列尾〔⋯〕與成員抽屜共用；SPEC #vms-web-787「成員」）：能做什麼（抽屜）、變更角色…（第 2 級確認）、
 * 組織權限…、停用 / 恢復、移出。沒有權限的項目不出現（UX-11）。
 */
export function useMemberActions(org: MyOrg, opts: { inDrawer?: boolean } = {}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [, setSp] = useSearchParams();
  const admin = isOrgAdmin(org);
  const [editing, setEditing] = useState<OrgMember | null>(null);
  const [roleOf, setRoleOf] = useState<OrgMember | null>(null);
  const [removing, setRemoving] = useState<OrgMember | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const { ask, dialog } = useConfirm();
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['org-members', org.org_id] });
    void qc.invalidateQueries({ queryKey: ['ot', org.org_id, 'members'] });
    void qc.invalidateQueries({ queryKey: ['org-access', org.org_id] });
    void qc.invalidateQueries({ queryKey: ['me-orgs'] });
  };
  const status = useMutation({
    mutationFn: (m: OrgMember) => orgApi.memberStatus(org, m.user_id, { expected_version: m.version, status: m.status === 'active' ? 'suspended' : 'active' }),
    onSuccess: refresh,
    onError: (e) => setErr(errText(t, e)),
  });
  const items = (m: OrgMember): (MoreItem & { maintenance?: boolean })[] => {
    const other = !m.is_me && m.role !== 'owner';
    return [
      { key: 'access', label: t('iam.what_can', { name: memberName(m) }), hidden: opts.inDrawer, onClick: () => setSp((sp) => (sp.set('member', m.user_id), sp)) },
      { key: 'role', label: t('iam.change_role'), hidden: !canChangeRole(org, m), onClick: () => setRoleOf(m) },
      { key: 'perms', label: t('iam.edit_perms'), hidden: !admin || !other || m.role === 'org_admin', onClick: () => setEditing(m) },
      {
        key: 'status',
        label: m.status === 'active' ? t('people.suspend') : t('people.reactivate'),
        // 停用 / 移出只在成員抽屜最下方的「維護」區（SPEC IAM-10，第二輪 B2-06）；成員表格的〔⋯〕不列
        hidden: !admin || !other || !opts.inDrawer,
        maintenance: true,
        onClick: () => {
          if (m.status !== 'active') return status.mutate(m);
          void ask({ danger: true, title: t('iam.suspend_title', { name: memberName(m) }), body: t('iam.suspend_body'), action: t('people.suspend') }).then((ok) => ok && status.mutate(m));
        },
      },
      { key: 'remove', label: t('people.remove'), danger: true, maintenance: true, hidden: !admin || !other || !m.can_remove || !opts.inDrawer, onClick: () => setRemoving(m) },
    ];
  };
  const dialogs = (
    <>
      {err && <p className="error">{err}</p>}
      <PermDialog org={org} member={editing} close={() => setEditing(null)} done={refresh} />
      <ChangeRoleDialog org={org} member={roleOf} close={() => setRoleOf(null)} done={refresh} />
      <RemoveDialog org={org} member={removing} close={() => (setRemoving(null), refresh())} />
      {dialog}
    </>
  );
  return { items, dialogs };
}

export function MembersTab({ org }: { org: MyOrg }) {
  const { t } = useTranslation();
  const { session } = useApp();
  const qc = useQueryClient();
  const [, setSp] = useSearchParams();
  const admin = isOrgAdmin(org);
  const members = useQuery({ queryKey: ['org-members', org.org_id], queryFn: () => orgApi.members(org) });
  const invites = useQuery({ queryKey: ['org-invites', org.org_id], queryFn: () => orgApi.invitations(org), enabled: admin });
  const [inviting, setInviting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const me = session?.user?.user_id;
  const act = useMemberActions(org);

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['org-members', org.org_id] });
    void qc.invalidateQueries({ queryKey: ['org-invites', org.org_id] });
    void qc.invalidateQueries({ queryKey: ['me-orgs'] });
  };
  const revoke = useMutation({ mutationFn: (id: string) => orgApi.revokeInvite(org, id), onSuccess: refresh, onError: (e) => setErr(errText(t, e)) });

  const pending = (invites.data ?? []).filter((i) => (!i.status || i.status === 'pending') && new Date(i.expires_at) > new Date());

  return (
    <section className="card">
      <div className="toolbar">
        <h2 className="section-title">{t('people.members_of', { name: org.display_name })}</h2>
        <div className="spacer" />
        {admin && (
          <button className="btn btn-primary btn-sm" onClick={() => setInviting(true)}>
            {t('people.invite')}
          </button>
        )}
      </div>
      <p className="muted small">{t(org.kind === 'personal' ? 'people.members_hint_personal' : 'people.members_hint')}</p>
      {err && <p className="error">{err}</p>}
      {members.isLoading && <p className="muted">{t('common.loading')}</p>}
      {members.isError && <p className="error">{errText(t, members.error)}</p>}
      <div className="table-wrap">
        <table className="grid ot-cards">
          <thead>
            <tr>
              <th>{t('people.name')}</th>
              <th>{t('iam.org_role')}</th>
              <th>{t('people.status')}</th>
              <th>{t('people.joined')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {(members.data ?? []).map((m) => (
              <tr key={m.user_id} data-member={m.user_id}>
                <td data-label={t('people.name')}>
                  <button className="link-btn" onClick={() => setSp((sp) => (sp.set('member', m.user_id), sp))}>
                    {memberName(m)}
                  </button>
                  {(m.is_me || m.user_id === me) && <span className="muted small"> ({t('transfer.me')})</span>}
                  {m.duty_mode && <span className="chip">{t('people.duty_badge')}</span>}
                  {m.display_name && m.email && <div className="muted small">{m.email}</div>}
                </td>
                <td data-label={t('iam.org_role')}>{t(`people.org_role_${m.role}`, { defaultValue: t('iam.org_role_other') })}</td>
                <td data-label={t('people.status')}>
                  <span className={'badge ' + (m.status === 'active' ? 'badge-ok' : 'badge-warn')}>{t(`people.status_${m.status}`, { defaultValue: t('common.state_unknown') })}</span>
                </td>
                <td data-label={t('people.joined')} className="small muted">
                  {dateStamp(m.joined_at)}
                </td>
                <td>
                  <MoreMenu label={t('iam.more_of', { name: memberName(m) })} items={act.items(m)} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {admin && pending.length > 0 && (
        <>
          <h3 className="section-title">{t('people.pending_invites')}</h3>
          <ul className="plain-list">
            {pending.map((i) => (
              <li key={i.invite_id} className="row" data-invite={i.invite_id}>
                <span>{i.email}</span>
                <span className="chip">{t(`people.org_role_${i.role}`, { defaultValue: i.role })}</span>
                {i.invited_by_name && <span className="muted small">{t('people.invited_by', { name: i.invited_by_name })}</span>}
                <span className="muted small">{t('people.expires', { date: dateStamp(i.expires_at) })}</span>
                <div className="spacer" />
                <button className="btn btn-sm" onClick={() => revoke.mutate(i.invite_id)}>
                  {t('people.revoke_invite')}
                </button>
              </li>
            ))}
          </ul>
        </>
      )}

      {act.dialogs}
      <InviteDialog org={org} open={inviting} close={() => setInviting(false)} done={refresh} />
    </section>
  );
}

/** 變更角色（第 2 級確認：寫出變更前後能做什麼；只有 owner 能給 / 拿掉組織管理員）。 */
function ChangeRoleDialog({ org, member, close, done }: { org: MyOrg; member: OrgMember | null; close(): void; done(): void }) {
  const { t } = useTranslation();
  const [role, setRole] = useState('');
  const [forId, setForId] = useState('');
  const [err, setErr] = useState<string | null>(null);
  if (member && member.user_id !== forId) {
    setForId(member.user_id);
    setRole(member.role);
    setErr(null);
  }
  const save = useMutation({
    mutationFn: () => orgApi.memberRole(org, member!.user_id, role, member!.version),
    onSuccess: () => {
      done();
      close();
    },
    onError: (e) => setErr(errText(t, e)),
  });
  const roles = member ? [...new Set([member.role, ...assignableRoles(org)])] : [];
  const desc = (r: string) => t(`iam.org_role_desc_${r}`, { defaultValue: '' });
  return (
    <Dialog open={!!member} title={t('iam.change_role_of', { name: memberName(member ?? undefined) })} onClose={close}>
      {member && (
        <div className="form" data-testid="change-role">
          <label className="field">
            <span>{t('iam.new_role')}</span>
            <select className="input" value={role} onChange={(e) => setRole(e.target.value)}>
              {roles.map((r) => (
                <option key={r} value={r}>
                  {t(`people.org_role_${r}`, { defaultValue: t('iam.org_role_other') })}
                </option>
              ))}
            </select>
          </label>
          {role !== member.role && (
            <div className="notice small" data-testid="role-diff">
              <div>{t('iam.role_before', { role: t(`people.org_role_${member.role}`, { defaultValue: member.role }), desc: desc(member.role) })}</div>
              <div>{t('iam.role_after', { role: t(`people.org_role_${role}`, { defaultValue: role }), desc: desc(role) })}</div>
            </div>
          )}
          {err && <div className="error">{err}</div>}
          <div className="dialog-actions">
            <button className="btn" onClick={close}>
              {t('common.cancel')}
            </button>
            <button className="btn btn-primary" disabled={save.isPending || role === member.role} onClick={() => save.mutate()}>
              {t('iam.change_role_action')}
              {t('ot.reauth_suffix')}
            </button>
          </div>
        </div>
      )}
    </Dialog>
  );
}

function PermDialog({ org, member, close, done }: { org: MyOrg; member: OrgMember | null; close(): void; done(): void }) {
  const { t } = useTranslation();
  const [perms, setPerms] = useState<string[]>([]);
  const [roles, setRoles] = useState<string[]>([]);
  const [duty, setDuty] = useState(false);
  const [forId, setForId] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const team = org.kind === 'team';
  if (member && member.user_id !== forId) {
    setForId(member.user_id);
    setPerms(member.org_permissions);
    setRoles(member.service_roles ?? []);
    setDuty(!!member.duty_mode);
    setErr(null);
  }
  const save = useMutation({
    mutationFn: async () => {
      if (team && JSON.stringify([...roles].sort()) !== JSON.stringify([...(member!.service_roles ?? [])].sort())) {
        await orgShareApi.setServiceRoles(org, member!.user_id, roles);
      }
      if (duty !== !!member!.duty_mode) await orgShareApi.setDutyMode(org, member!.user_id, duty);
      const fresh = (await orgApi.members(org)).find((m) => m.user_id === member!.user_id);
      return orgApi.memberStatus(org, member!.user_id, { expected_version: fresh?.version ?? member!.version, org_permissions: perms });
    },
    onSuccess: () => {
      done();
      close();
    },
    onError: (e) => setErr(errText(t, e)),
  });
  return (
    <Dialog open={!!member} title={t('people.edit_perms_of', { name: memberName(member ?? undefined) })} onClose={close}>
      <div className="form">
        <p className="muted small">{t('people.perms_hint')}</p>
        {ORG_PERMS.map((p) => (
          <label key={p} className="check">
            <input type="checkbox" checked={perms.includes(p)} onChange={(e) => setPerms(e.target.checked ? [...perms, p] : perms.filter((x) => x !== p))} />
            <span>{t(`people.perm_${p.replace('.', '_')}`)}</span>
            <span className="muted small">{t(`people.perm_${p.replace('.', '_')}_desc`)}</span>
          </label>
        ))}
        {team && (
          <label className="check">
            <input type="checkbox" checked={duty} onChange={(e) => setDuty(e.target.checked)} />
            <span>{t('people.duty_mode')}</span>
            <span className="muted small">{t('people.duty_mode_desc')}</span>
          </label>
        )}
        {team && (
          <fieldset className="field">
            <legend>{t('svc.service_roles')}</legend>
            <p className="muted small">{t('svc.service_roles_hint')}</p>
            {SERVICE_ROLES.map((r) => (
              <label key={r} className="check">
                <input type="checkbox" checked={roles.includes(r)} onChange={(e) => setRoles(e.target.checked ? [...roles, r] : roles.filter((x) => x !== r))} />
                <span>{t(`svc.role_${r}`)}</span>
                <span className="muted small">{t(`svc.role_${r}_desc`)}</span>
              </label>
            ))}
          </fieldset>
        )}
        {err && <div className="error">{err}</div>}
        <div className="row-end">
          <span className="small muted">{t('people.stepup_note')}</span>
          <div className="spacer" />
          <button className="btn" onClick={close}>
            {t('common.cancel')}
          </button>
          <button className="btn btn-primary" disabled={save.isPending} onClick={() => save.mutate()}>
            {t('common.save')}
          </button>
        </div>
      </div>
    </Dialog>
  );
}

export function InviteDialog({ org, open, close, done }: { org: MyOrg; open: boolean; close(): void; done(): void }) {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const roles = assignableRoles(org);
  const [role, setRole] = useState('member');
  const [result, setResult] = useState<InviteResult | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // 403 EMAIL_NOT_VERIFIED →「先驗證你的 email」（platform#176）
  const verify = useEmailVerifyPrompt(() => invite.mutate());
  const invite = useMutation({
    mutationFn: () => orgApi.invite(org, { email: email.trim(), role }),
    onSuccess: (r) => {
      setResult(r);
      done();
    },
    onError: (e) => verify.ask(e) || setErr(errText(t, e)),
  });
  function reset() {
    setEmail('');
    setResult(null);
    setErr(null);
    close();
  }
  function submit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    invite.mutate();
  }
  const here = result?.status === 'invited' ? acceptInviteLink(window.location.origin, result.token, org.env) : '';
  return (
    <>
    <Dialog open={open} title={t('people.invite')} onClose={reset}>
      {result ? (
        <div className="form" data-testid="invite-result" data-status={result.status}>
          {result.status === 'joined' ? (
            <p className="notice">{t('people.invite_joined', { email })}</p>
          ) : (
            <>
              <p className="notice">{t('people.invite_link_body', { email })}</p>
              <code className="mono copyable" data-testid="invite-url">
                {result.invite_url}
              </code>
              <div className="row">
                <button className="btn btn-sm" onClick={() => void navigator.clipboard?.writeText(result.invite_url)}>
                  {t('common.copy')}
                </button>
              </div>
              <p className="small muted">{t('people.invite_here')}</p>
              <code className="mono copyable small">{here}</code>
            </>
          )}
          <div className="row-end">
            <button className="btn btn-primary btn-sm" onClick={reset}>
              {t('common.done')}
            </button>
          </div>
        </div>
      ) : (
        <form className="form" onSubmit={submit}>
          <p className="muted small">{t(org.kind === 'personal' ? 'people.invite_body_personal' : 'people.invite_body')}</p>
          <label className="field">
            <span>{t('auth.email')}</span>
            <input className="input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} autoFocus />
          </label>
          <label className="field">
            <span>{t('people.role')}</span>
            <select className="input" value={role} onChange={(e) => setRole(e.target.value)}>
              {roles.map((r) => (
                <option key={r} value={r}>
                  {t(`people.org_role_${r}`, { defaultValue: r })}
                </option>
              ))}
            </select>
            <span className="small muted">{t(org.kind === 'personal' ? 'people.invite_role_hint_personal' : 'people.invite_role_hint')}</span>
          </label>
          {err && <div className="error">{err}</div>}
          <div className="row-end">
            <button type="button" className="btn" onClick={reset}>
              {t('common.cancel')}
            </button>
            <button className="btn btn-primary" disabled={invite.isPending}>
              {t('people.send_invite')}
            </button>
          </div>
        </form>
      )}
    </Dialog>
    {verify.prompt}
    </>
  );
}
